import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  EllipsisVerticalIcon,
  MoveDiagonalIcon,
  PlusIcon,
  Trash2Icon
} from 'lucide-react';
import { Link } from 'react-router';

import Header from '../Header';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';
import useOutletContext from '../useOutletContext';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Empty, EmptyContent } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

interface ListRvaCalculators {
  rvaCalculators: {
    __typename: 'RVACalculatorNode';
    calculatorFundAnalyses: [] | null;
    createDate: null | string;
    createUid: {
      __typename: 'UserNode';
      id: string;
      name: null | string;
    } | null;
    id: string;
    industry: null | string;
    name: null | string;
    totalIrrNetLevered: null | number;
    totalMoicNetLevered: null | number;
    transactionName: null | string;
  }[];
}

const LIST_RVA_CALCULATORS = gql`
  query ListRVACalculators($opportunityId: ID) {
    rvaCalculators(opportunityId: $opportunityId) {
      ... on RVACalculatorNode {
        id
        name
        transactionName
        industry
        createUid {
          id
          name
        }
        createDate
        totalIrrNetLevered
        totalMoicNetLevered
        calculatorFundAnalyses {
          fund {
            name
          }
          testRelativeValue
        }
      }
    }
  }
`;

const InvestmentsDirectIdDealCalculator = () => {
  const { id, loading } = useOutletContext();

  const { data, loading: loading1 } = useQuery<
    ListRvaCalculators,
    { opportunityId: string }
  >(LIST_RVA_CALCULATORS, { variables: { opportunityId: id } });

  if (loading || loading1) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (!data) {
    return <div>{'Error'}</div>;
  }

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <div className="grid grid-cols-[1fr_auto]">
          <div className="min-w-0 space-y-2.5 px-5 pt-5 pb-16.5 md:pl-14">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Deal Calculator</h2>
              {data.rvaCalculators.length !== 0 && (
                <Button asChild>
                  <Link to="new-calculation">
                    <PlusIcon />
                    New Calculation
                  </Link>
                </Button>
              )}
            </div>
            <div className="bg-card rounded-md border p-5">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-0" />
                    <TableHead className="text-xs leading-none font-semibold">
                      Calculation Name
                    </TableHead>
                    <TableHead className="text-xs leading-none font-semibold">
                      Calculated Date
                    </TableHead>
                    <TableHead className="text-xs leading-none font-semibold">
                      Calculated By
                    </TableHead>
                    <TableHead className="w-0" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rvaCalculators.length === 0 ? (
                    <TableRow className="hover:bg-transparent">
                      <TableCell
                        className="p-0"
                        colSpan={5}
                      >
                        <Empty className="px-0! pt-5! pb-0!">
                          <EmptyContent>
                            <Button asChild>
                              <Link to="new-calculation">
                                <PlusIcon />
                                New Calculation
                              </Link>
                            </Button>
                          </EmptyContent>
                        </Empty>
                      </TableCell>
                    </TableRow>
                  ) : (
                    [...data.rvaCalculators]
                      .sort((a, b) => {
                        const dateA = a.createDate ?? '';
                        const dateB = b.createDate ?? '';
                        return dateB.localeCompare(dateA);
                      })
                      .map(({ createDate, createUid, id, name }) => (
                        <TableRow key={id}>
                          <TableCell className="w-0">
                            <Button
                              asChild
                              size="icon"
                              variant="ghost"
                            >
                              <Link to={id}>
                                <MoveDiagonalIcon />
                              </Link>
                            </Button>
                          </TableCell>
                          <TableCell>{name}</TableCell>
                          <TableCell>{createDate?.split('T')[0]}</TableCell>
                          <TableCell>
                            {createUid?.name && (
                              <div className="flex items-center gap-x-2">
                                <Avatar>
                                  <AvatarFallback>
                                    {createUid.name.split(' ').map(a => a[0])}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium">
                                  {createUid.name}
                                </span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="w-0">
                            <DropdownMenu modal={false}>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  aria-label="Open menu"
                                  size="icon-sm"
                                  variant="ghost"
                                >
                                  <EllipsisVerticalIcon />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                className="w-56"
                              >
                                <DropdownMenuGroup>
                                  <DropdownMenuItem variant="destructive">
                                    <Trash2Icon />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectIdDealCalculator;
